import { getFrotaDisponibilidadeById as getDisponibilidade, updateFrotaDisponibilidade as updateDisponibilidade, deleteFrotaDisponibilidade as deleteDisponibilidade, getVeiculos, getUltimaPosicaoPorPlaca } from "@spark/services/frotas";
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Form } from 'react-final-form';
import { Nav } from 'react-bootstrap';
import { BorderedBottomTabs, CustomNavLink, NavItem } from '@spark/ui-components/components/Tabs';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { formStructure, formSections } from './formConfig';
import { gerarLinkComTokenCriptografado, gerarLinkAcessoDireto } from '@spark/utils/functions/gerarLinkComTokenCriptografado';
import { useAuth } from '../../../../../context/AuthContext';
import { dataFormatada } from '@spark/utils/formatters/dataFormatada';
import { searchMotoristaOptions, searchGestorOptions } from '@spark/utils/functions/excelLoader';
import { updateMultipleFieldsByName } from '@spark/ui-components/utils/formHelpers';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import CopyButton from '@spark/ui-components/components/CopyLink';
import PageHeader from '@spark/ui-components/components/HeaderForm';
import ConfirmDeleteModal from '../../../../UI/Modals/index2';
import Icon from '@spark/ui-components/components/Icon';
import { getPositionModalErrorMessage } from '../../../../DisponibilidadeFrotaPatio/utils/positions';

const mapVeiculoToOption = (veiculo = {}) => {
  const id = veiculo?.Id ?? veiculo?.id ?? veiculo?.IdVeiculo ?? veiculo?.idVeiculo;
  if (id === undefined || id === null || id === '') {
    return null;
  }

  const frotaNumero = veiculo?.Frota ?? veiculo?.frota ?? '';
  const placa = veiculo?.Placa ?? veiculo?.placa ?? '';
  const modeloDescricao = veiculo?.ModeloVeicular?.Descricao ?? veiculo?.ModeloVeicular?.descricao ?? veiculo?.ModeloVeicular?.NomeTipoModeloVeicular ?? veiculo?.modeloVeicular?.Descricao ?? veiculo?.modeloVeicular?.descricao ?? '';
  const labelParts = [frotaNumero, placa, modeloDescricao].filter(Boolean);
  const label = labelParts.length ? labelParts.join(' - ') : `Veiculo ${id}`;

  return {
    value: String(id),
    label,
    data: veiculo
  };
};

const extractVeiculoSelection = selected => {
  if (!selected) {
    return {
      idVeiculo: null,
      frotaDescricao: '',
      placa: '',
      label: ''
    };
  }

  if (typeof selected === 'object') {
    const rawId = selected.value ?? selected.id ?? selected.Id ?? selected.data?.Id ?? selected.data?.id ?? selected.data?.IdVeiculo ?? selected.data?.idVeiculo ?? null;
    const hasRawId = rawId !== null && rawId !== undefined && rawId !== '';
    const parsedId = hasRawId ? Number(rawId) : null;
    const idVeiculo = !hasRawId ? null : Number.isNaN(parsedId) ? rawId : parsedId;
    const data = selected.data ?? {};
    const frotaDescricao = data.Frota ?? data.frota ?? selected.label ?? '';
    const placa = data.Placa ?? data.placa ?? '';
    const modelo = data.ModeloVeicular ?? data.modeloVeicular ?? null;
    const modeloDescricao = modelo?.Descricao ?? modelo?.descricao ?? '';
    const computedLabel = selected.label || [frotaDescricao, placa].filter(Boolean).join(' - ') || (idVeiculo !== null ? `Veiculo ${idVeiculo}` : '');
    return {
      idVeiculo,
      frotaDescricao,
      placa,
      modeloDescricao,
      label: computedLabel
    };
  }

  const label = String(selected || '');
  const hasRawId = selected !== null && selected !== undefined && selected !== '';
  const parsedId = hasRawId ? Number(selected) : null;
  const idVeiculo = !hasRawId ? null : Number.isNaN(parsedId) ? selected : parsedId;

  return {
    idVeiculo,
    frotaDescricao: label,
    placa: '',
    modeloDescricao: '',
    label
  };
};

const sanitizePlate = valor => {
  if (!valor) return '';
  return valor.toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
};

const extractPlateFromRecord = registro => {
  if (!registro) return '';
  const candidatos = [registro?.Frota, registro?.frota, registro?.Veiculo, registro?.veiculo, registro?.Placa, registro?.placa];
  const MIN_PLATE_LENGTH = 5;

  for (const valor of candidatos) {
    const clean = sanitizePlate(valor);
    if (clean.length >= MIN_PLATE_LENGTH) {
      return clean;
    }
  }

  return '';
};

const extractFleetFromRecord = registro => {
  if (!registro) return '';

  const candidatos = [
    registro?.FrotaDescricao,
    registro?.frotaDescricao,
    registro?.NumeroFrota,
    registro?.numeroFrota,
    registro?.Frota,
    registro?.frota
  ];

  for (const valor of candidatos) {
    const normalizado = String(valor || '').trim();

    if (normalizado) return normalizado;
  }

  return '';
};

const buildUltimaPosicaoPayload = registro => {
  const placa = extractPlateFromRecord(registro);
  const frota = extractFleetFromRecord(registro);

  return {
    ...(placa ? { placa } : {}),
    ...(frota ? { frota } : {})
  };
};

const parseDataPosicao = valor => {
  if (!valor) return null;

  const tentaParser = raw => {
    const normalizado = String(raw).replace(' ', 'T');
    const data = new Date(normalizado);
    if (!Number.isNaN(data.getTime())) return data;

    const [dataParte, horaParte] = String(raw).split(' ');
    if (dataParte && horaParte) {
      const [ano, mes, dia] = dataParte.split('-').map(Number);
      const [hora, minuto, segundo] = horaParte.split(':').map(Number);
      const dataFallback = new Date(ano, (mes || 1) - 1, dia || 1, hora || 0, minuto || 0, segundo || 0);
      if (!Number.isNaN(dataFallback.getTime())) return dataFallback;
    }

    return null;
  };

  return tentaParser(valor) || tentaParser(`${valor}Z`);
};

const formatarDataPosicao = valor => {
  if (!valor) return '';
  try {
    return dataFormatada(valor);
  } catch (err) {
    const data = parseDataPosicao(valor);
    return !data ? valor : data.toLocaleString('pt-BR');
  }
};

const isPosicaoAntiga = valor => {
  if (!valor) return false;
  const data = parseDataPosicao(valor);
  if (!data) return false;
  const agora = new Date();
  const diffDias = (agora.getTime() - data.getTime()) / (1000 * 60 * 60 * 24);
  return diffDias >= 7;
};

const getProviderLogo = provider => {
  if (!provider) return null;
  const normalized = String(provider).toLowerCase();
  if (normalized.includes('angel')) {
    return { src: '/img/angelira.png', alt: 'Angellira' };
  }
  if (normalized.includes('sighra')) {
    return { src: '/img/sighra.png', alt: 'Sighra' };
  }
  if (normalized.includes('a52')) {
    return { src: '/img/a52.png', alt: 'A52' };
  }
  return null;
};

const mapApiToForm = dados => {
  const calcularDiasParados = dataDisponibilidade => {
    if (!dataDisponibilidade) return 0;
    const dataAtual = new Date();
    const dataDisponibilidadeObj = new Date(dataDisponibilidade);
    const diferencaMs = dataAtual.getTime() - dataDisponibilidadeObj.getTime();
    const diferencaDias = Math.floor(diferencaMs / (1000 * 60 * 60 * 24));
    return Math.max(0, diferencaDias);
  }; // Calcular dias aguardando descarga com base na DataAgendamento da API

  const calcularDiasAguardandoDescarga = dataAgendamento => {
    if (!dataAgendamento) return 0;
    const dataAtual = new Date();
    const dataAgendamentoObj = new Date(dataAgendamento);
    const diferencaMs = dataAtual.getTime() - dataAgendamentoObj.getTime();
    const diferencaDias = Math.floor(diferencaMs / (1000 * 60 * 60 * 24));
    return Math.max(0, diferencaDias);
  }; // Funï¿½ï¿½o para converter string de data para objeto Date

  const converterParaDate = dataString => {
    if (!dataString) return null;
    const dataObj = new Date(dataString);
    return isNaN(dataObj.getTime()) ? null : dataObj;
  }; // Log para debug dos dados recebidos

  const idVeiculoInicial = dados?.IdVeiculo ?? dados?.idVeiculo ?? dados?.VeiculoId ?? dados?.IdVeiculoDisponibilidade ?? null;
  const frotaNumeroInicial = dados?.Frota ?? dados?.frota ?? '';
  const placaInicial = dados?.Veiculo ?? dados?.veiculo ?? dados?.Placa ?? dados?.placa ?? '';
  const frotaOptionInicial = (() => {
    if (idVeiculoInicial !== undefined && idVeiculoInicial !== null && idVeiculoInicial !== '') {
      const option = mapVeiculoToOption({
        Id: idVeiculoInicial,
        Frota: frotaNumeroInicial,
        Placa: placaInicial,
        ModeloVeicular: dados?.ModeloVeicular ?? dados?.modeloVeicular ?? null
      });
      if (option) {
        return option;
      }
      const labelParts = [frotaNumeroInicial, placaInicial].filter(Boolean);
      return {
        value: String(idVeiculoInicial),
        label: labelParts.length ? labelParts.join(' - ') : 'Veiculo ' + idVeiculoInicial,
        data: {
          Id: idVeiculoInicial,
          Frota: frotaNumeroInicial,
          Placa: placaInicial,
          ModeloVeicular: dados?.ModeloVeicular ?? dados?.modeloVeicular ?? null
        }
      };
    }
    const labelParts = [frotaNumeroInicial, placaInicial].filter(Boolean);
    if (labelParts.length) {
      return labelParts.join(' - ');
    }
    return frotaNumeroInicial || '';
  })();

  const mappedData = {
    frota: frotaOptionInicial,
    tipoModelo: (dados?.ModeloVeicular?.Descricao ?? dados?.ModeloVeicular?.descricao ?? dados?.ModeloVeicular?.NomeTipoModeloVeicular ?? dados?.TipoModelo ?? ''),
    motorista: dados.NomeMotorista || '',
    segmento: 'Nï¿½o informado',
    destino: dados.Destino || '',
    responsavelComercial: dados.NomeComercial || '',
    uf: dados.LocalEstado || '',
    diasParados: calcularDiasParados(dados.DataDisponibilidade),
    diasAguardandoDescarga: calcularDiasAguardandoDescarga(dados.DataAgendamento),
    dataAgendamento: converterParaDate(dados.DataAgendamento),
    DataDisponibilidade: converterParaDate(dados.DataDisponibilidade),
    gestor: dados.NomeGestor || '',
    vazioem: dados.LocalCidade || '',
    descargaPendente: dados.DescargaPendente === 1,
    vazio: dados.Vazio === 1,
    coleta: dados.Coleta === 1,
    negociacao: dados.Negociacao === 1
  }; // Log para debug dos dados mapeados

  return mappedData;
};

const mapFormToApi = values => {
  // Formatar a data do formulï¿½rio para o formato da API (YYYY-MM-DD)
  const formatarDataParaAPI = data => {
    if (!data) {
      // Se nï¿½o houver data, retornar null
      return null;
    } // Se for um objeto Date, converter para string

    if (data instanceof Date) {
      return data.toISOString().slice(0, 19).replace('T', ' ');
    } // Se for uma string, tentar converter

    try {
      const dataObj = new Date(data); // Verificar se a data ï¿½ vï¿½lida

      if (isNaN(dataObj.getTime())) {
        return null;
      }

      return dataObj.toISOString().slice(0, 19).replace('T', ' ');
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return null;
    }
  };

  const dataModificacao = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const frotaSelecionada = extractVeiculoSelection(values.frota);
  const frotaId = frotaSelecionada.idVeiculo ?? null;
  const frotaDescricao = frotaSelecionada.frotaDescricao || frotaSelecionada.label;
  const tipoModeloSelecionado = typeof values.tipoModelo === 'object' ? (values.tipoModelo?.label ?? values.tipoModelo?.value ?? '') : values.tipoModelo;
  const resolvedTipoModelo = tipoModeloSelecionado || frotaSelecionada.modeloDescricao || '';

  return {
    DataDisponibilidade: formatarDataParaAPI(values.DataDisponibilidade),
    DataAgendamento: formatarDataParaAPI(values.dataAgendamento),
    DiasAguardandoDescarga: values.diasAguardandoDescarga || 0,
    LocalCidade: values.vazioem,
    LocalEstado: values.uf,
    IdVeiculo: frotaId,
    Frota: frotaId ?? frotaDescricao,
    Veiculo: frotaSelecionada.placa || '',
    TipoModelo: resolvedTipoModelo,
    NomeMotorista: typeof values.motorista === 'object' ? values.motorista?.value : values.motorista,
    NomeGestor: typeof values.gestor === 'object' ? values.gestor?.value : values.gestor,
    DescargaPendente: values.descargaPendente ? 1 : 0,
    Vazio: values.vazio ? 1 : 0,
    Coleta: values.coleta ? 1 : 0,
    Negociacao: values.negociacao ? 1 : 0,
    NomeComercial: values.responsavelComercial,
    Destino: values.destino,
    DataModificacao: dataModificacao,
    UsuarioModificacao: 'usuario2'
  };
};

const getDisponibilidadeById = async id => {
  const response = await getDisponibilidade(id);
  return response;
};

const updateDisponibilidadeById = async (id, payload) => {
  const response = await updateDisponibilidade(id, payload);
  return response;
};

const deleteDisponibilidadeById = async id => {
  await deleteDisponibilidade(id);
};

const Forms = () => {
  const { id } = useParams();
  const [dadosRecebidos, setDadosRecebidos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [ultimaPosicao, setUltimaPosicao] = useState(null);
  const [loadingPosicao, setLoadingPosicao] = useState(false);
  const [erroPosicao, setErroPosicao] = useState(null);
  const [tabAtiva, setTabAtiva] = useState('dados');

  const renderCardPosicao = () => (
    <Card className="mt-3 w-100">
      <CardBody>
        <h5 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.85rem', letterSpacing: '0.08em' }}>
          Localizacao do veiculo
        </h5>

        <p className="text-muted small mb-3">
          Consulta automatica utilizando a placa <strong>{placaParaConsulta || 'nao informada'}</strong>.
        </p>

        {loadingPosicao && <p className="text-muted mb-2">Buscando localizacao no provedor externo...</p>}

        {!loadingPosicao && !placaParaConsulta && <div className="alert alert-info py-2 mb-3 mb-md-4">
          Nao foi possivel identificar a placa desta disponibilidade para consultar o mapa.
        </div>}

        {erroPosicao && <div className="alert alert-warning bg-warning text-dark border-warning py-2 mb-3 mb-md-4">
          {erroPosicao}
        </div>}

        {ultimaPosicao && <div className="mb-3 small text-muted d-flex flex-wrap gap-3">
          <span><strong>Placa:</strong> {ultimaPosicao.placa}</span>
          {ultimaPosicao.cidade && <span><strong>Local:</strong> {`${ultimaPosicao.cidade}${ultimaPosicao.estado ? `/${ultimaPosicao.estado}` : ''}`}</span>}
          {ultimaPosicao.dataPosicao && <span className="d-inline-flex align-items-center gap-2"><strong>Atualizacao:</strong> {formatarDataPosicao(ultimaPosicao.dataPosicao)} {isPosicaoAntiga(ultimaPosicao.dataPosicao) && <span title="Atualizacao ha mais de 7 dias"><Icon name="alert-circle-outline" size={16} color="#f0ad4e" /></span>}</span>}
          {ultimaPosicao.integracao && <span><strong>Fonte:</strong> {ultimaPosicao.integracao?.provider ?? ultimaPosicao.integracao?.Provider ?? ultimaPosicao.integracao}</span>}
        </div>}

        {ultimaPosicao && <div className="rounded overflow-hidden shadow-sm w-100" style={{
          border: '1px solid #e5e7eb',
          minHeight: '360px'
        }}>
          <iframe
            title="google-maps-ultima-posicao"
            src={`https://www.google.com/maps?q=${encodeURIComponent(`${ultimaPosicao.latitude},${ultimaPosicao.longitude}`)}&z=16&output=embed`}
            width="100%"
            height="360"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          {(() => {
            const logo = getProviderLogo(ultimaPosicao.integracao?.provider ?? ultimaPosicao.integracao?.Provider ?? ultimaPosicao.integracao);
            if (!logo) return null;
            return <div className="d-flex justify-content-end p-2">
              <img src={logo.src} alt={logo.alt} style={{ height: '32px', width: 'auto', opacity: 0.85 }} />
            </div>;
          })()}
        </div>}
      </CardBody>
    </Card>
  );
  
  const formRef = useRef(null);
  const formatVeiculoOption = useCallback(mapVeiculoToOption, []);
  const handleSearchFrotas = useCallback(async (searchTerm = '') => {
    const termo = String(searchTerm || '').trim();
    if (!termo) return [];

    try {
      const response = await getVeiculos({
        frota: termo,
        quantidadePorPagina: 10,
        pagina: 1,
        limit: 10
      });

      const registrosResponse = response?.registros ?? response?.Registros ?? response?.data?.registros ?? response?.data?.Registros ?? response?.veiculos ?? response?.Veiculos ?? [];
      const registros = Array.isArray(registrosResponse) ? registrosResponse : [];
      const options = registros.map(formatVeiculoOption).filter(Boolean);

      const unique = [];
      const seen = new Set();
      options.forEach(option => {
        if (option && !seen.has(option.value)) {
          seen.add(option.value);
          unique.push(option);
        }
      });

      return unique;
    } catch (error) {
      console.error('Erro ao buscar frotas:', error);
      return [];
    }
  }, [formatVeiculoOption]);

  const handleFrotaSelect = useCallback((selectedOption) => {
    const currentForm = formRef.current;
    if (!currentForm) return;

    if (!selectedOption) {
      currentForm.change('tipoModelo', '');
      return;
    }

    const data = selectedOption.data ?? {};
    const modelo = data.ModeloVeicular ?? data.modeloVeicular ?? null;
    const descricao = modelo?.Descricao ?? modelo?.descricao ?? modelo?.NomeTipoModeloVeicular ?? '';
    const fallbackDescricao = data?.Descricao ?? data?.NomeTipoModeloVeicular ?? '';

    const labelDescricao = typeof selectedOption?.label === 'string'
      ? selectedOption.label.split(' - ').pop()?.trim()
      : '';

    const resolvedDescricao = (descricao || fallbackDescricao || labelDescricao || '').trim();

    currentForm.change('tipoModelo', resolvedDescricao);
  }, []);

  const location = useLocation();
  const isPersonalizado = location.pathname.includes('/detalhesdisponibilidade/personalizado/');
  const { user } = useAuth();
  const history = useHistory();

  const linkSeguro = gerarLinkComTokenCriptografado(`${process.env.REACT_APP_BASE_URL}/pages/programacao/detalhesdisponibilidade/personalizado/${id}`, 'TOKEN-ACESSO-DIRETO-TRANSBEN');

  const linkComToken = user?.accessToken ? gerarLinkAcessoDireto(`${process.env.REACT_APP_BASE_URL}/pages/programacao/detalhesdisponibilidade/personalizado/${id}`, user.accessToken, {
    name: user.name,
    email: user.email,
    expiresAt: user.expiresAt
  }) : linkSeguro;

  const placaParaConsulta = useMemo(() => extractPlateFromRecord(dadosRecebidos), [dadosRecebidos]);
  const payloadPosicao = useMemo(() => buildUltimaPosicaoPayload(dadosRecebidos), [dadosRecebidos]);

  useEffect(() => {
    if (!id) return;
    getDisponibilidadeById(id).then(setDadosRecebidos).catch(() => alert('Erro ao carregar dados da disponibilidade.')).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    let ativo = true;

    if (tabAtiva !== 'posicao') {
      setLoadingPosicao(false);
      return () => {
        ativo = false;
      };
    }

    if (!placaParaConsulta) {
      setUltimaPosicao(null);
      setErroPosicao(null);
      setLoadingPosicao(false);
      return () => {
        ativo = false;
      };
    }

    setLoadingPosicao(true);
    setErroPosicao(null);

    getUltimaPosicaoPorPlaca(payloadPosicao).then(response => {
      if (!ativo) return;
      const posicao = response?.posicao || {};
      const latitude = Number(posicao?.latitude ?? posicao?.Latitude);
      const longitude = Number(posicao?.longitude ?? posicao?.Longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setUltimaPosicao(null);
        setErroPosicao('Localização não encontrada para a placa informada.');
        return;
      }

      setUltimaPosicao({
        latitude,
        longitude,
        placa: response?.placa || placaParaConsulta,
        dataPosicao: posicao?.dataPosicao ?? posicao?.DataPosicao ?? null,
        cidade: posicao?.cidade ?? posicao?.Cidade ?? '',
        estado: posicao?.estado ?? posicao?.Estado ?? '',
        logradouro: posicao?.logradouro ?? posicao?.Logradouro ?? '',
        integracao: response?.integracao ?? null
      });
    }).catch(error => {
      if (!ativo) return;
      const friendly = getPositionModalErrorMessage(error);
          
      setErroPosicao(friendly);
      setUltimaPosicao(null);

    }).finally(() => {
      if (ativo) setLoadingPosicao(false);
    });

    return () => { ativo = false; };
  }, [payloadPosicao, placaParaConsulta, tabAtiva]);

  const onSubmit = useCallback(async values => {
    try {
      setSaving(true);
      const payload = mapFormToApi(values);
      await updateDisponibilidadeById(id, payload);
      history.goBack();
    } catch (err) {
      alert(`Erro ao atualizar os dados: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [id, history]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteDisponibilidadeById(id);
      history.goBack();
    } catch (err) {
      alert(`Erro ao deletar: ${err.message}`);
    } finally {
      setDeleting(false);
      setModalVisible(false);
    }
  };

  const testarToken = async () => {
    try {
      const userData = localStorage.getItem('user');

      const response = await getDisponibilidade(id);
      alert('Token enviado com sucesso! Verifique o console para mais detalhes.');
    } catch (error) {
      alert(`Erro na requisiÃ§Ã£o: ${error.message}`);
    }
  };

  const gerarLinkTeste = () => {
    const tokenTeste = 'TOKEN-TESTE-TRANSBEN-123';
    const linkTeste = gerarLinkAcessoDireto(`http://localhost:3000/pages/programacao/detalhesdisponibilidade/personalizado/${id}`, tokenTeste, {
      name: 'UsuÃ¡rio Teste',
      email: 'teste@transben.com.br'
    });

    navigator.clipboard.writeText(linkTeste);
    alert('Link de teste copiado! Acesse o link e clique em "Testar Token" para verificar.');
  };

  const initialValues = useMemo(() => {
    if (!dadosRecebidos) return {};
    const mappedValues = mapApiToForm(dadosRecebidos);
    return mappedValues;
  }, [dadosRecebidos]);

  const updatedFormStructure = useMemo(() => {
    const fieldUpdates = {
      frota: {
        onSearch: handleSearchFrotas,
        customOnChange: handleFrotaSelect
      },
      motorista: {
        onSearch: searchMotoristaOptions
      },
      gestor: {
        onSearch: searchGestorOptions
      }
    };
    return updateMultipleFieldsByName(formStructure, fieldUpdates);
  }, [handleSearchFrotas, handleFrotaSelect]);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>
      <p>Carregando dados da disponibilidade...</p>
    </div>;
  }

  return <>
    <Form onSubmit={onSubmit} initialValues={initialValues}>
      {({ handleSubmit, form, values }) => {

        const { reset } = form;
        formRef.current = form;

        return <Card>
          <CardBody>

            <Card>
              <div className="d-flex justify-content-between align-items-center px-3 py-2">
                {isPersonalizado && <PageHeader titulo={"EdiÃ§Ã£o Externa"} subtitulo={"Modo de acesso direto - EdiÃ§Ã£o restrita"} />}


                {!isPersonalizado && <div className="d-flex align-items-center gap-2">
                  <CopyButton textToCopy={linkComToken} tooltip="Copiar Link com AutenticaÃ§Ã£o" copiedTooltip="Link copiado!" placement="bottom" dir="ltr" iconSize="18px" />

                  {user?.accessToken && <CopyButton textToCopy={linkSeguro} tooltip="Copiar Link BÃ¡sico" copiedTooltip="Link bÃ¡sico copiado!" placement="bottom" dir="ltr" iconSize="18px" />}


                </div>}

                {isPersonalizado && <div className="d-flex align-items-center gap-2">
                  <Button variant="outline-primary" size="sm" onClick={() => history.push('/log_in')} style={{
                    fontSize: '12px'
                  }}>

                    <i className="fas fa-sign-in-alt"></i> Fazer Login
                  </Button>
                </div>}

              </div>
            </Card>

            <BorderedBottomTabs className="mt-3">
              <Nav variant="tabs" className="nav-tabs">
                <NavItem>
                  <CustomNavLink active={tabAtiva === 'dados'} onClick={() => setTabAtiva('dados')}>
                    <span className="tab-text">Dados</span>
                  </CustomNavLink>
                </NavItem>
                <NavItem>
                  <CustomNavLink active={tabAtiva === 'posicao'} onClick={() => setTabAtiva('posicao')}>
                    <span className="tab-text">Posicao</span>
                  </CustomNavLink>
                </NavItem>
              </Nav>
            </BorderedBottomTabs>

            {tabAtiva === 'dados' && (
              <div className="mt-3">
                <FormContainer onSubmit={handleSubmit}>
                  <DynamicForm structure={updatedFormStructure} sections={formSections(initialValues)} />

                  <FormButtonToolbar className="mt-5 d-flex justify-content-between align-items-center" style={{
                    width: '100%'
                  }}>
                    <div className="d-flex gap-2">
                      <ButtonWithLoading variant="primary" type="submit" loading={saving ? 'true' : undefined}>

                        Salvar
                      </ButtonWithLoading>
                      {isPersonalizado ? <Button variant="secondary" type="button" onClick={() => history.goBack()}>
                        Fechar
                      </Button> : <Button variant="secondary" type="button" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>}

                    </div>
                    {!isPersonalizado && <Button variant="danger" type="button" onClick={() => setModalVisible(true)}>

                      Deletar
                    </Button>}

                  </FormButtonToolbar>
                </FormContainer>
              </div>
            )}

            {tabAtiva === 'posicao' && renderCardPosicao()}
          </CardBody>
        </Card>;
      }}
    </Form>

    <ConfirmDeleteModal show={modalVisible} onClose={() => setModalVisible(false)} onConfirm={handleDelete} loading={deleting} />

  </>;
};

export default Forms;
